import { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./Expenses.module.css";

/** Mirrors ExpenseController.ALLOWED_CATEGORIES on the backend — keep the
 *  two lists in sync when broadening. */
const CATEGORIES = [
  "Utilities", "Rent", "Supplies", "Maintenance", "Transportation",
  "Salaries & Wages", "Marketing", "Insurance", "Miscellaneous",
];

/** Curated per-category color so the pill palette reads as designed, not
 *  hashed-random. Falls back to a neutral gray for any future category
 *  the frontend hasn't been updated to color yet. */
const CATEGORY_COLOR = {
  "Utilities":         { bg: "#e2e9fb", fg: "#2c3e7b" },
  "Rent":               { bg: "#f3e2fb", fg: "#6b2c8f" },
  "Supplies":           { bg: "#dff5e0", fg: "#1f6a2d" },
  "Maintenance":        { bg: "#fdf1d4", fg: "#a07a1a" },
  "Transportation":     { bg: "#e0f3f7", fg: "#1a6d80" },
  "Salaries & Wages":   { bg: "#fde9e4", fg: "#c0392b" },
  "Marketing":          { bg: "#fbe2ec", fg: "#a02361" },
  "Insurance":          { bg: "#e7e4fb", fg: "#4a3b9c" },
  "Miscellaneous":      { bg: "#f4efe3", fg: "#6a5d45" },
};
const FALLBACK_COLOR = { bg: "#f0ebe4", fg: "#6a5d45" };

const METHODS = ["CASH", "BANK", "GCASH", "CARD", "OTHER"];

const RANGES = [
  { value: "30",     label: "Last 30 days" },
  { value: "90",     label: "Last 90 days" },
  { value: "365",    label: "Last 12 months" },
  { value: "all",    label: "All time" },
  { value: "custom", label: "Custom range…" },
];

function isoToday(dayOffset = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (dayOffset) d.setDate(d.getDate() + dayOffset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isoDateToStart(iso) {
  if (!iso || typeof iso !== "string") return NaN;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

/** Convert a window choice into a {from, to} pair of epoch-ms bounds,
 *  compared against each expense's own date. Same shape as the range
 *  picker already used in Reports → Finance, so switching between the
 *  two pages feels familiar. */
function computeBounds(range, customFrom, customTo) {
  if (range === "all") return { from: 0, to: Infinity };
  if (range === "custom") {
    const from = isoDateToStart(customFrom);
    const to   = isoDateToStart(customTo);
    return {
      from: Number.isNaN(from) ? 0        : from,
      to:   Number.isNaN(to)   ? Infinity : to + 24 * 60 * 60 * 1000,
    };
  }
  const from = Date.now() - Number(range) * 24 * 60 * 60 * 1000;
  return { from, to: Infinity };
}

function peso(v) {
  return `₱${Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });
}

function getCurrentUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/** Animates a number counting up from its previous value to `value`
 *  whenever `value` changes — the summary cards' headline figures.
 *  ~500ms ease-out, skipped entirely under prefers-reduced-motion. */
function useCountUp(value, duration = 550) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) { setDisplay(value); fromRef.current = value; return; }

    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);

    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return display;
}

function CategoryPill({ category }) {
  const c = CATEGORY_COLOR[category] || FALLBACK_COLOR;
  return (
    <span className={s.pill} style={{ background: c.bg, color: c.fg }}>{category}</span>
  );
}

export default function ExpensesPage() {
  const currentUser = getCurrentUser();
  const canManage = currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin";

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [range, setRange]         = useState("30");
  const [customFrom, setCustomFrom] = useState(isoToday(-30));
  const [customTo, setCustomTo]     = useState(isoToday());
  const [category, setCategory]   = useState("");
  const [q, setQ]                 = useState("");

  const [sortBy, setSortBy]   = useState("expenseDate");
  const [sortDir, setSortDir] = useState("desc");

  const [modal, setModal] = useState(null); // { mode: "add" | "edit", expense? } | { mode: "void", expense }
  const [toast, setToast] = useState(null); // { kind: "ok" | "err", text }
  const toastTimer = useRef(null);

  function showToast(kind, text) {
    clearTimeout(toastTimer.current);
    setToast({ kind, text });
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  async function load() {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/api/expenses`);
      if (!res.ok) throw new Error(`Failed to load expenses (${res.status})`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadError(e.message || "Failed to load expenses.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const bounds = useMemo(() => computeBounds(range, customFrom, customTo), [range, customFrom, customTo]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter(r => {
      const t = isoDateToStart(r.expenseDate);
      if (!Number.isNaN(t) && (t < bounds.from || t >= bounds.to)) return false;
      if (category && r.category !== category) return false;
      if (term) {
        const hay = `${r.description || ""} ${r.payee || ""} ${r.referenceNo || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, bounds, category, q]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (sortBy === "amount") { av = Number(av || 0); bv = Number(bv || 0); }
      else { av = String(av || ""); bv = String(bv || ""); }
      const cmp = typeof av === "number" ? av - bv : av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortBy, sortDir]);

  const activeRows = useMemo(() => filtered.filter(r => r.status !== "VOIDED"), [filtered]);
  const totalActive = activeRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const countActive = activeRows.length;
  /** Every category with at least one active expense in the current
   *  filtered range, sorted highest-spend first. Feeds both the "Top
   *  category" summary card (categoryBreakdown[0]) and the full
   *  per-category breakdown grid below it. */
  const categoryBreakdown = useMemo(() => {
    const byCat = new Map();
    for (const r of activeRows) {
      const cat = r.category || "Miscellaneous";
      const cur = byCat.get(cat) || { cat, amt: 0, count: 0 };
      cur.amt += Number(r.amount || 0);
      cur.count += 1;
      byCat.set(cat, cur);
    }
    return [...byCat.values()].sort((a, b) => b.amt - a.amt);
  }, [activeRows]);
  const topCategory = categoryBreakdown[0] || null;

  const totalDisplay = useCountUp(totalActive);
  const countDisplay = useCountUp(countActive, 400);

  function toggleSort(key) {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "amount" || key === "expenseDate" ? "desc" : "asc"); }
  }

  async function submitExpense(payload, editingId) {
    const url = editingId ? `${API_BASE}/api/expenses/${editingId}` : `${API_BASE}/api/expenses`;
    const method = editingId ? "PUT" : "POST";
    const headers = { "Content-Type": "application/json" };
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (!res.ok) {
      throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
    }
    await load();
  }

  async function voidExpense(id, reason) {
    const res = await fetch(`${API_BASE}/api/expenses/${id}/void`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      throw new Error(await extractErrorMessage(res, `Void failed (${res.status})`));
    }
    await load();
  }

  const columns = [
    { key: "expenseDate", label: "Date" },
    { key: "category",    label: "Category" },
    { key: "description", label: "Description" },
    { key: "payee",       label: "Paid To" },
    { key: "amount",      label: "Amount" },
    { key: "status",      label: "Status" },
  ];

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.eyebrow}>ADMIN · OPERATIONS</div>
        <h1 className={s.title}>Expenses</h1>
        <p className={s.sub}>
          Record and track school expenses — utilities, rent, supplies, and everything else going out.
        </p>
      </header>

      <div className={s.summaryGrid}>
        <div className={`${s.summaryCard} ${s.summaryCardAccent}`}>
          <div className={s.summaryLabel}>Total in range</div>
          <div className={s.summaryValue}>{peso(totalDisplay)}</div>
          <div className={s.summarySub}>{RANGES.find(r => r.value === range)?.label || "Selected range"}</div>
        </div>
        <div className={s.summaryCard}>
          <div className={s.summaryLabel}>Entries</div>
          <div className={s.summaryValue}>{Math.round(countDisplay)}</div>
          <div className={s.summarySub}>expenses recorded</div>
        </div>
        <div className={s.summaryCard}>
          <div className={s.summaryLabel}>Top category</div>
          <div className={s.summaryValue}>{topCategory ? peso(topCategory.amt) : "—"}</div>
          <div className={s.summarySub}>{topCategory ? topCategory.cat : "No expenses yet"}</div>
        </div>
      </div>

      {categoryBreakdown.length > 0 && (
        <div className={s.categorySection}>
          <div className={s.categorySectionHead}>
            <h3>Expenses by category</h3>
            <span className={s.categorySectionCount}>
              {categoryBreakdown.length} categor{categoryBreakdown.length === 1 ? "y" : "ies"}
            </span>
          </div>
          <div className={s.categoryGrid}>
            {categoryBreakdown.map(c => {
              const pct = totalActive > 0 ? (c.amt / totalActive) * 100 : 0;
              return (
                <div key={c.cat} className={s.categoryCard}>
                  <div className={s.categoryCardHead}>
                    <CategoryPill category={c.cat} />
                    <span className={s.categoryCardAmount}>{peso(c.amt)}</span>
                  </div>
                  <div className={s.categoryCardMeta}>
                    {c.count} entr{c.count === 1 ? "y" : "ies"} · {pct.toFixed(1)}% of total
                  </div>
                  <div className={s.categoryCardBar}>
                    <div className={s.categoryCardBarFill}
                         style={{
                           width: `${Math.min(100, pct)}%`,
                           background: (CATEGORY_COLOR[c.cat] || FALLBACK_COLOR).fg,
                         }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={s.filterBar}>
        <div className={s.filterField}>
          <span>Range</span>
          <select value={range} onChange={e => setRange(e.target.value)}>
            {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {range === "custom" && (
          <>
            <div className={s.filterField}>
              <span>From</span>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            </div>
            <div className={s.filterField}>
              <span>To</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </div>
          </>
        )}
        <div className={s.filterField}>
          <span>Category</span>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className={`${s.filterField} ${s.searchField}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <input
              type="search"
              placeholder="Description, payee, reference…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
        </div>
        {canManage && (
          <button type="button" className={s.btnPrimary} onClick={() => setModal({ mode: "add" })}>
            + Add Expense
          </button>
        )}
      </div>

      {loadError ? (
        <div className={s.errorBanner}>{loadError}</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                {columns.map(col => {
                  const active = sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`${s.sortable} ${active ? s.sortActive : ""}`}
                      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                      onClick={() => toggleSort(col.key)}
                    >
                      {col.label}
                      <span className={s.sortArrow}>{active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}</span>
                    </th>
                  );
                })}
                {canManage && <th className={s.actionsCol}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className={s.skeletonRow}>
                    {columns.map(c => <td key={c.key}><span className={s.skeletonBar} /></td>)}
                    {canManage && <td><span className={s.skeletonBar} /></td>}
                  </tr>
                ))
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (canManage ? 1 : 0)} className={s.emptyRow}>
                    No expenses match the current filters.
                  </td>
                </tr>
              ) : sorted.map((r, i) => {
                const voided = r.status === "VOIDED";
                return (
                  <tr
                    key={r.id}
                    className={`${s.row} ${s.rowClickable} ${voided ? s.rowVoided : ""}`}
                    style={{ animationDelay: `${Math.min(i, 12) * 0.03}s` }}
                    title={voided ? `Voided: ${r.voidReason || "—"}` : "Click for full details"}
                    onClick={() => setModal({ mode: "view", expense: r })}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setModal({ mode: "view", expense: r });
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <td className={s.dateCell}>{formatDate(r.expenseDate)}</td>
                    <td><CategoryPill category={r.category} /></td>
                    <td className={s.descCell}>{r.description}</td>
                    <td>{r.payee || "—"}</td>
                    <td className={s.amountCell}>{peso(r.amount)}</td>
                    <td>
                      <span className={`${s.statusBadge} ${voided ? s.statusVoided : s.statusActive}`}>
                        {voided ? "Voided" : "Active"}
                      </span>
                    </td>
                    {canManage && (
                      <td className={s.actionsCol}>
                        {!voided && (
                          <>
                            <button type="button" className={s.btnGhost}
                                    onClick={e => { e.stopPropagation(); setModal({ mode: "edit", expense: r }); }}>
                              Edit
                            </button>
                            <button type="button" className={s.btnDanger}
                                    onClick={e => { e.stopPropagation(); setModal({ mode: "void", expense: r }); }}>
                              Void
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (modal.mode === "add" || modal.mode === "edit") && (
        <ExpenseFormModal
          expense={modal.expense || null}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            try {
              await submitExpense(payload, modal.expense?.id);
              setModal(null);
              showToast("ok", modal.expense ? "Expense updated." : "Expense recorded.");
            } catch (e) {
              throw e;
            }
          }}
        />
      )}

      {modal && modal.mode === "void" && (
        <VoidExpenseModal
          expense={modal.expense}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await voidExpense(modal.expense.id, reason);
              setModal(null);
              showToast("ok", "Expense voided.");
            } catch (e) {
              throw e;
            }
          }}
        />
      )}

      {modal && modal.mode === "view" && (
        <ViewExpenseModal
          expense={modal.expense}
          canManage={canManage}
          onClose={() => setModal(null)}
          onEdit={() => setModal({ mode: "edit", expense: modal.expense })}
          onVoid={() => setModal({ mode: "void", expense: modal.expense })}
        />
      )}

      {toast && (
        <div className={`${s.toast} ${toast.kind === "err" ? s.toastErr : s.toastOk}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

function ExpenseFormModal({ expense, onClose, onSubmit }) {
  const editing = !!expense;
  const [expenseDate, setExpenseDate] = useState(expense?.expenseDate || "");
  const [category, setCategoryVal]    = useState(expense?.category || "");
  const [description, setDescription] = useState(expense?.description || "");
  const [amount, setAmount]           = useState(expense?.amount != null ? String(expense.amount) : "");
  const [paymentMethod, setPaymentMethod] = useState(expense?.paymentMethod || "");
  const [payee, setPayee]             = useState(expense?.payee || "");
  const [referenceNo, setReferenceNo] = useState(expense?.referenceNo || "");
  const [notes, setNotes]             = useState(expense?.notes || "");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fn = e => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose, busy]);

  const valid = expenseDate && category && description.trim()
    && Number.isFinite(Number(amount)) && Number(amount) > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      await onSubmit({
        expenseDate,
        category,
        description: description.trim(),
        amount: Number(amount),
        paymentMethod: paymentMethod || null,
        payee: payee.trim() || null,
        referenceNo: referenceNo.trim() || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      setError(err.message || "Save failed.");
      setBusy(false);
    }
  }

  return (
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalPinned}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>{editing ? "Edit Expense" : "Add Expense"}</h3>
          <button className={s.modalClose} type="button" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className={s.modalForm}>
          <div className={s.modalScrollArea}>
            {error && <div className={s.error}>{error}</div>}

            <div className={s.field}>
              <label className={s.label}>Date <span className={s.req}>*</span></label>
              <input
                className={s.input}
                type="date"
                value={expenseDate}
                onChange={e => setExpenseDate(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Category <span className={s.req}>*</span></label>
              <select className={s.input} value={category} onChange={e => setCategoryVal(e.target.value)} required>
                <option value="" disabled>Select a category…</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className={s.field}>
              <label className={s.label}>Description <span className={s.req}>*</span></label>
              <input
                className={s.input}
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. September electric bill"
                maxLength={200}
                required
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Amount (₱) <span className={s.req}>*</span></label>
              <input
                className={s.input}
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Payment method <span className={s.fieldOpt}>(optional)</span></label>
              <select className={s.input} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                <option value="">— not specified —</option>
                {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className={s.field}>
              <label className={s.label}>Paid to <span className={s.fieldOpt}>(optional)</span></label>
              <input
                className={s.input}
                type="text"
                value={payee}
                onChange={e => setPayee(e.target.value)}
                placeholder="Vendor / supplier / landlord"
                maxLength={200}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Reference # <span className={s.fieldOpt}>(optional)</span></label>
              <input
                className={s.input}
                type="text"
                value={referenceNo}
                onChange={e => setReferenceNo(e.target.value)}
                placeholder="Receipt / invoice number"
                maxLength={64}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Notes <span className={s.fieldOpt}>(optional)</span></label>
              <textarea
                className={`${s.input} ${s.textarea}`}
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any additional context…"
              />
            </div>
          </div>
          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={s.btnPrimary} disabled={!valid || busy}>
              {busy ? "Saving…" : editing ? "Save Changes" : "Add Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VoidExpenseModal({ expense, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    const fn = e => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose, busy]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reason.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.message || "Void failed.");
      setBusy(false);
    }
  }

  return (
    <div className={s.modalOverlay}>
      <div className={s.modal} style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Void Expense</h3>
          <button className={s.modalClose} type="button" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{expense.description}</div>
          <div className={s.modalSubMeta}>
            <span>{formatDate(expense.expenseDate)}</span>
            <span>· {peso(expense.amount)}</span>
          </div>
        </div>
        <form onSubmit={handleSubmit} className={s.modalForm}>
          {error && <div className={s.error}>{error}</div>}
          <div className={s.field}>
            <label className={s.label}>Reason <span className={s.req}>*</span></label>
            <textarea
              className={`${s.input} ${s.textarea}`}
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Duplicate entry, wrong amount…"
              autoFocus
              required
            />
            <div className={s.fieldHint}>
              Voiding keeps this entry on record but removes it from every total. This can't be undone from here.
            </div>
          </div>
          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={s.btnDangerSolid} disabled={!reason.trim() || busy}>
              {busy ? "Voiding…" : "Void Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Read-only detail view — opened by clicking any row. Shows every field
 *  the table can't fit inline, most importantly the full Description and
 *  Notes text (the table truncates/omits both). Everyone who can see the
 *  list can open this; Edit/Void stay hidden for non-managers (same gate
 *  as the table's action column). */
function ViewExpenseModal({ expense, canManage, onClose, onEdit, onVoid }) {
  const voided = expense.status === "VOIDED";

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalPinned}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Expense Details</h3>
          <button className={s.modalClose} type="button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.modalScrollArea}>
          <div className={s.viewTop}>
            <CategoryPill category={expense.category} />
            <span className={`${s.statusBadge} ${voided ? s.statusVoided : s.statusActive}`}>
              {voided ? "Voided" : "Active"}
            </span>
          </div>

          <div className={s.viewAmount}>{peso(expense.amount)}</div>

          <div className={s.viewSection}>
            <div className={s.viewLabel}>Description</div>
            <div className={s.viewValue}>{expense.description || "—"}</div>
          </div>

          <div className={s.viewGrid}>
            <div className={s.viewField}>
              <div className={s.viewLabel}>Date</div>
              <div className={s.viewValue}>{formatDate(expense.expenseDate)}</div>
            </div>
            <div className={s.viewField}>
              <div className={s.viewLabel}>Payment method</div>
              <div className={s.viewValue}>{expense.paymentMethod || "—"}</div>
            </div>
            <div className={s.viewField}>
              <div className={s.viewLabel}>Paid to</div>
              <div className={s.viewValue}>{expense.payee || "—"}</div>
            </div>
            <div className={s.viewField}>
              <div className={s.viewLabel}>Reference #</div>
              <div className={s.viewValue}>{expense.referenceNo || "—"}</div>
            </div>
          </div>

          <div className={s.viewSection}>
            <div className={s.viewLabel}>Notes</div>
            <div className={`${s.viewValue} ${!expense.notes ? s.viewValueMuted : ""}`}>
              {expense.notes || "No notes added."}
            </div>
          </div>

          {voided && (
            <div className={s.viewVoidBanner}>
              <b>Voided</b> — {expense.voidReason || "No reason given."}
              {(expense.voidedByUsername || expense.voidedAt) && (
                <div className={s.viewMeta}>
                  {expense.voidedByUsername ? `by ${expense.voidedByUsername}` : ""}
                  {expense.voidedAt ? ` on ${formatDateTime(expense.voidedAt)}` : ""}
                </div>
              )}
            </div>
          )}

          <div className={s.viewAudit}>
            {expense.addedByUsername && (
              <span>Recorded by {expense.addedByUsername}{expense.addedAt ? ` · ${formatDateTime(expense.addedAt)}` : ""}</span>
            )}
            {expense.updatedByUsername && expense.updatedByUsername !== expense.addedByUsername && (
              <span>· Last updated by {expense.updatedByUsername}{expense.updatedAt ? ` · ${formatDateTime(expense.updatedAt)}` : ""}</span>
            )}
          </div>
        </div>
        <div className={s.modalActions}>
          <button type="button" className={s.btnGhost} onClick={onClose}>Close</button>
          {canManage && !voided && (
            <>
              <button type="button" className={s.btnDanger} onClick={onVoid}>Void</button>
              <button type="button" className={s.btnPrimary} onClick={onEdit}>Edit</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
